import React, { useContext, useEffect, useState } from "react";
import { salaryFilter } from "../schemas";
import { useFormik } from "formik";
import UserServices from "../../UserServices/UserServices";
import Pagination from "react-js-pagination";
import useTable from "../../hooks/useTable";
import useSortableData from "../../hooks/useSortableData";
import CurrencyFormat from "react-currency-format";
import { Link, useParams } from "react-router-dom";
import jsPDF from "jspdf";
import { NoteState } from "../../context/notes/NoteState";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Payroll cycle: 26th of previous month → 25th of selected month */
const getPayrollDates = (year, month) => {
  const y = parseInt(year, 10);
  const m = parseInt(month, 10);
  let startY = y;
  let startM = m - 1;
  if (startM < 1) {
    startM = 12;
    startY = y - 1;
  }
  return {
    start_date: `${startY}-${String(startM).padStart(2, "0")}-26`,
    end_date: `${y}-${String(m).padStart(2, "0")}-25`,
  };
};

const mapSalaryRow = (values, monthLabel = "", monthNum = null) => ({
  id: values.id,
  name: values.name,
  department: values.department,
  medical: parseInt(values.medical) || 0,
  reward: parseInt(values.reward) || 0,
  joining: values.joining,
  advance_total: parseInt(values.advance) || 0,
  fine_total: parseInt(values.fine) || 0,
  designation: values.designation,
  salary: parseInt(values.salary) || 0,
  detections: parseInt(values.detections) || 0,
  cafe: parseInt(values.cafe) || 0,
  commission: parseInt(values.commission?.commission) || 0,
  commission_per: parseInt(values.commission?.commission_per) || 0,
  gx_commission: parseInt(values.commission?.gx_commission) || 0,
  pt_commission: parseInt(values.commission?.pt_commission) || 0,
  small_pt_commission: parseInt(values.commission?.small_pt_commission) || 0,
  loans: parseInt(values.loan) || 0,
  month: monthLabel,
  monthNum,
});

export default function SatffProfileSalary() {
  const { state } = useContext(NoteState);
  const { branch_id, role, UId } = state;
  const { id } = useParams();
  const currentDate = new Date();
  currentDate.setDate(1);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;
  const formattedMonth = month < 10 ? `0${month}` : String(month);

  const [loading, setLoading] = useState(false);
  const [myData, setMyData] = useState([]);
  const [periodMode, setPeriodMode] = useState("monthly"); // monthly | yearly
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState("25");
  const [totalResults, setTotalResults] = useState("0");
  const [totalPages, setTotalPages] = useState("1");
  const [designtion, setDesigntion] = useState("");

  const fetchMonthSalary = async (yearValue, monthValue, userId) => {
    const { start_date, end_date } = getPayrollDates(yearValue, monthValue);
    const response = await UserServices.getHrSalaryRecords({
      branch_id,
      start_date,
      end_date,
      user_id: userId,
      limit: 25,
      page: 1,
    });
    return response;
  };

  const getSalary = async (mode = periodMode, formValues = null) => {
    const filterValues = formValues || values;
    const userId = filterValues.trainer_id || (role === "9" ? UId : id);
    setLoading(true);

    try {
      if (mode === "yearly") {
        const months = Array.from({ length: 12 }, (_, i) => i + 1);
        const settled = await Promise.all(
          months.map(async (m) => {
            try {
              const response = await fetchMonthSalary(
                filterValues.edate,
                m,
                userId
              );
              if (response.status === 200 && Array.isArray(response.data.data)) {
                const row = response.data.data[0];
                if (row) {
                  return mapSalaryRow(row, MONTH_NAMES[m - 1], m);
                }
              }
            } catch (err) {
              if (err.response?.status !== 404) {
                console.log(err);
              }
            }
            return null;
          })
        );
        const rows = settled.filter(Boolean);
        if (rows[0]?.designation) setDesigntion(rows[0].designation);
        setMyData(rows);
        setLimit("25");
        setTotalResults(String(rows.length));
        setTotalPages("1");
        setCurrentPage(1);
      } else {
        const response = await fetchMonthSalary(
          filterValues.edate,
          filterValues.sdate,
          userId
        );
        if (response.status === 200) {
          const rows = (response.data.data || []).map((row) =>
            mapSalaryRow(
              row,
              MONTH_NAMES[parseInt(filterValues.sdate, 10) - 1] || "",
              parseInt(filterValues.sdate, 10)
            )
          );
          if (rows[0]?.designation) setDesigntion(rows[0].designation);
          setMyData(rows);
          setLimit(response.data.pagination?.per_page ?? limit);
          setTotalResults(response.data.pagination?.total_record ?? rows.length);
          setTotalPages(response.data.pagination?.total_pages ?? 1);
          setCurrentPage(response.data.pagination?.current_page ?? 1);
        }
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setTotalResults("0");
        setTotalPages("1");
        setMyData([]);
      } else {
        console.log(err);
      }
    }
    setLoading(false);
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    getSalary();
  }, []);

  const initialValues = {
    sdate: formattedMonth,
    edate: String(year),
    trainer_id: role === "9" ? UId : id,
  };

  const { values, errors, touched, handleBlur, handleChange, handleSubmit } =
    useFormik({
      initialValues: initialValues,
      validationSchema: salaryFilter,
      onSubmit: (formValues) => {
        getSalary(periodMode, formValues);
      },
    });

  const changePagination = (e) => {
    if (periodMode === "yearly") {
      setCurrentPage(e);
      return;
    }
    setCurrentPage(e);
    let pageNo = e;
    const getPaginationData = async () => {
      setLoading(true);
      try {
        const { start_date, end_date } = getPayrollDates(values.edate, values.sdate);
        const responce = await UserServices.getHrSalaryRecords({
          branch_id,
          start_date,
          end_date,
          user_id: values.trainer_id,
          limit,
          page: pageNo,
        });

        if (responce.status === 200) {
          const rows = (responce.data.data || []).map((row) =>
            mapSalaryRow(
              row,
              MONTH_NAMES[parseInt(values.sdate, 10) - 1] || "",
              parseInt(values.sdate, 10)
            )
          );
          setMyData(rows);
          setLimit(responce.data.pagination?.per_page ?? limit);
          setTotalResults(responce.data.pagination?.total_record ?? rows.length);
          setTotalPages(responce.data.pagination?.total_pages ?? 1);
          setCurrentPage(responce.data.pagination?.current_page ?? pageNo);
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setTotalResults("0");
          setTotalPages("1");
          setMyData([]);
        }
      }
      setLoading(false);
    };
    getPaginationData();
  };
  const changeLimit = (e) => {
    let newLimit = e;
    setLimit(newLimit);
    if (periodMode === "yearly") return;
    const getLimitData = async () => {
      setLoading(true);
      try {
        const { start_date, end_date } = getPayrollDates(values.edate, values.sdate);
        const responce = await UserServices.getHrSalaryRecords({
          branch_id,
          start_date,
          end_date,
          user_id: values.trainer_id,
          limit: newLimit,
          page: 1,
        });

        if (responce.status === 200) {
          const rows = (responce.data.data || []).map((row) =>
            mapSalaryRow(
              row,
              MONTH_NAMES[parseInt(values.sdate, 10) - 1] || "",
              parseInt(values.sdate, 10)
            )
          );
          setMyData(rows);
          setLimit(responce.data.pagination?.per_page ?? newLimit);
          setTotalResults(responce.data.pagination?.total_record ?? rows.length);
          setTotalPages(responce.data.pagination?.total_pages ?? 1);
          setCurrentPage(responce.data.pagination?.current_page ?? 1);
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setTotalResults("0");
          setTotalPages("1");
          setMyData([]);
        }
      }
      setLoading(false);
    };
    getLimitData();
  };

  const generatePdf = (table) => {
    const doc = new jsPDF("p", "mm", "letter");

    // Loop through all the tables in the section and add them to the PDF

    // Add a new page for each table (skip first table as it is already on the first page)
    const captionText = table.getAttribute("data-caption");
    // Add the table caption as regular text above the table
    doc.text(captionText, 10, 10);
    const tableOptions = {
      headStyles: { fillColor: [232, 0, 43], textColor: 255 },
      styles: { fontSize: 5 }, // Adjust the font size here to fit the table content
    };
    doc.autoTable({
      html: table,
      ...tableOptions,
    });

    doc.save("Staff_Members_Salary.pdf");
  };

  const Table = ({ data, rowsPerPage }) => {
    const { slice } = useTable(data, 1, rowsPerPage);
    const { items, requestSort, sortConfig } = useSortableData(slice);

    const getClassNamesFor = (name) => {
      if (!sortConfig) {
        return;
      }
      return sortConfig.key === name ? sortConfig.direction : undefined;
    };

    let totalNetSalary = 0;
    let totalSalary = 0;

    const calculateFirstMonthSalary = (joiningDate, totalSalary, monthNum) => {
      const joiningDateObj = new Date(joiningDate);
      const selectedMonth = parseInt(monthNum || values.sdate, 10);
      const selectedYear = parseInt(values.edate, 10);

      if (
        joiningDateObj.getFullYear() === selectedYear &&
        joiningDateObj.getMonth() + 1 === selectedMonth
      ) {
        const remainingDays =
          new Date(
            joiningDateObj.getFullYear(),
            joiningDateObj.getMonth() + 1,
            0
          ).getDate() -
          joiningDateObj.getDate() +
          1;
        const dailySalary =
          totalSalary /
          new Date(
            joiningDateObj.getFullYear(),
            joiningDateObj.getMonth() + 1,
            0
          ).getDate();

        const firstMonthSalary = dailySalary * remainingDays;

        return firstMonthSalary.toFixed(0);
      } else if (selectedYear > joiningDateObj.getFullYear()) {
        return totalSalary.toFixed(0);
      } else if (
        joiningDateObj.getFullYear() === selectedYear &&
        selectedMonth >= joiningDateObj.getMonth() + 1
      ) {
        return totalSalary;
      } else {
        return 0;
      }
    };

    return loading ? (
      <SkeletonTheme height="40px">
        <Skeleton count={5} />
      </SkeletonTheme>
    ) : (
      <>
        <div className="table-responsive mt-3">
          <table
            className="table table-striped table-borderless table-style-1"
            id="report-POS"
            data-caption="Staff Members Salary"
          >
            <thead>
              <tr>
                <th scope="col" className="no_sort">
                  Sr#
                </th>
                {periodMode === "yearly" ? (
                  <th
                    scope="col"
                    onClick={() => requestSort("monthNum")}
                    className={getClassNamesFor("monthNum")}
                  >
                    Month
                  </th>
                ) : null}
                <th
                  scope="col"
                  onClick={() => requestSort("department")}
                  className={getClassNamesFor("department")}
                >
                  Department
                </th>
                <th
                  scope="col"
                  onClick={() => requestSort("name")}
                  className={getClassNamesFor("name")}
                >
                  Staff Name
                </th>
                <th
                  scope="col"
                  onClick={() => requestSort("designation")}
                  className={getClassNamesFor("designation")}
                >
                  Designation
                </th>

                <th
                  scope="col"
                  onClick={() => requestSort("salary")}
                  className={getClassNamesFor("salary")}
                >
                  Salary
                </th>
                {designtion === "Trainer" ? (
                  <>
                    <th
                      scope="col"
                      onClick={() => requestSort("commission_per")}
                      className={getClassNamesFor("commission_per")}
                    >
                      PT %
                    </th>
                    <th
                      scope="col"
                      onClick={() => requestSort("pt_commission")}
                      className={getClassNamesFor("pt_commission")}
                    >
                      PT Commission
                    </th>

                    <th
                      scope="col"
                      onClick={() => requestSort("gx_commission")}
                      className={getClassNamesFor("gx_commission")}
                    >
                      Studio Commission
                    </th>
                    <th
                      scope="col"
                      onClick={() => requestSort("small_pt_commission")}
                      className={getClassNamesFor("small_pt_commission")}
                    >
                      SPT Commission
                    </th>
                    <th
                      scope="col"
                      onClick={() => requestSort("commission")}
                      className={getClassNamesFor("commission")}
                    >
                      Total Commission
                    </th>
                  </>
                ) : null}

                <th
                  scope="col"
                  onClick={() => requestSort("reward")}
                  className={getClassNamesFor("reward")}
                >
                  Reward
                </th>
                <th
                  scope="col"
                  onClick={() => requestSort("advance_total")}
                  className={getClassNamesFor("advance_total")}
                >
                  Advance
                </th>
                <th
                  scope="col"
                  onClick={() => requestSort("fine_total")}
                  className={getClassNamesFor("fine_total")}
                >
                  Fine
                </th>
                <th
                  scope="col"
                  onClick={() => requestSort("loans")}
                  className={getClassNamesFor("loans")}
                >
                  Monthly Installment
                </th>
                <th
                  scope="col"
                  onClick={() => requestSort("cafe")}
                  className={getClassNamesFor("cafe")}
                >
                  Cafe
                </th>
                <th
                  scope="col"
                  onClick={() => requestSort("detections")}
                  className={getClassNamesFor("detections")}
                >
                  Deduction
                </th>
                <th
                  scope="col"
                  onClick={() => requestSort("medical")}
                  className={getClassNamesFor("medical")}
                >
                  Medical
                </th>
                <th scope="col" className="no_sort">
                  Net Salary
                </th>
              </tr>
            </thead>
            <tbody>
              {items.length ? (
                <>
                  {items.map((el, index) => (
                    <tr key={index}>
                      <td>
                        {currentPage === 1
                          ? index + 1
                          : (currentPage - 1) * limit + index + 1}
                      </td>
                      {periodMode === "yearly" ? <td>{el.month}</td> : null}
                      <td>{el.department}</td>

                      <td>
                        <Link to={"/staff-profile/" + el.id}>{el.name}</Link>
                      </td>
                      <td>{el.designation}</td>
                      <td>
                        <span className="d-none">
                          {
                            (totalSalary =
                              totalSalary +
                              parseInt(
                                calculateFirstMonthSalary(
                                  el.joining,
                                  el.salary,
                                  el.monthNum
                                )
                              ))
                          }
                        </span>
                        <CurrencyFormat
                          value={calculateFirstMonthSalary(
                            el.joining,
                            el.salary,
                            el.monthNum
                          )}
                          displayType={"text"}
                          thousandSpacing={"2s"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                          suffix={"/-"}
                          renderText={(value) => <span>{value}</span>}
                        />
                      </td>
                      {designtion === "Trainer" ? (
                        <>
                          <td>{el.commission_per + "%"}</td>
                          <td>
                            <CurrencyFormat
                              value={el.pt_commission}
                              displayType={"text"}
                              thousandSpacing={"2s"}
                              thousandSeparator={true}
                              prefix={"Rs "}
                              suffix={"/-"}
                              renderText={(value) => <span>{value}</span>}
                            />
                          </td>
                          <td>
                            <CurrencyFormat
                              value={el.gx_commission}
                              displayType={"text"}
                              thousandSpacing={"2s"}
                              thousandSeparator={true}
                              prefix={"Rs "}
                              suffix={"/-"}
                              renderText={(value) => <span>{value}</span>}
                            />
                          </td>
                          <td>
                            <CurrencyFormat
                              value={el.small_pt_commission}
                              displayType={"text"}
                              thousandSpacing={"2s"}
                              thousandSeparator={true}
                              prefix={"Rs "}
                              suffix={"/-"}
                              renderText={(value) => <span>{value}</span>}
                            />
                          </td>

                          <td>
                            <CurrencyFormat
                              value={el.commission}
                              displayType={"text"}
                              thousandSpacing={"2s"}
                              thousandSeparator={true}
                              prefix={"Rs "}
                              suffix={"/-"}
                              renderText={(value) => <span>{value}</span>}
                            />
                          </td>
                        </>
                      ) : null}
                      <td>
                        <CurrencyFormat
                          value={el.reward}
                          displayType={"text"}
                          thousandSpacing={"2s"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                          suffix={"/-"}
                          renderText={(value) => <span>{value}</span>}
                        />
                      </td>

                      <td>
                        <CurrencyFormat
                          value={el.advance_total}
                          displayType={"text"}
                          thousandSpacing={"2s"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                          suffix={"/-"}
                          renderText={(value) => <span>{value}</span>}
                        />
                      </td>
                      <td>
                        <CurrencyFormat
                          value={el.fine_total}
                          displayType={"text"}
                          thousandSpacing={"2s"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                          suffix={"/-"}
                          renderText={(value) => <span>{value}</span>}
                        />
                      </td>
                      <td>
                        <CurrencyFormat
                          value={el.loans}
                          displayType={"text"}
                          thousandSpacing={"2s"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                          suffix={"/-"}
                          renderText={(value) => <span>{value}</span>}
                        />
                      </td>
                      <td>
                        <CurrencyFormat
                          value={el.cafe}
                          displayType={"text"}
                          thousandSpacing={"2s"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                          suffix={"/-"}
                          renderText={(value) => <span>{value}</span>}
                        />
                      </td>
                      <td>
                        <CurrencyFormat
                          value={el.detections}
                          displayType={"text"}
                          thousandSpacing={"2s"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                          suffix={"/-"}
                          renderText={(value) => <span>{value}</span>}
                        />
                      </td>
                      <td>
                        <CurrencyFormat
                          value={el.medical}
                          displayType={"text"}
                          thousandSpacing={"2s"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                          suffix={"/-"}
                          renderText={(value) => <span>{value}</span>}
                        />
                      </td>
                      <td>
                        <span className="d-none">
                          {
                            (totalNetSalary =
                              totalNetSalary +
                              (parseInt(
                                calculateFirstMonthSalary(
                                  el.joining,
                                  el.salary,
                                  el.monthNum
                                )
                              ) +
                                el.commission +
                                el.reward -
                                el.advance_total -
                                el.fine_total -
                                el.cafe -
                                el.detections -
                                el.loans -
                                el.medical))
                          }
                        </span>
                        <CurrencyFormat
                          value={
                            parseInt(
                              calculateFirstMonthSalary(
                                el.joining,
                                el.salary,
                                el.monthNum
                              )
                            ) +
                            el.commission +
                            el.reward -
                            el.advance_total -
                            el.fine_total -
                            el.cafe -
                            el.detections -
                            el.loans -
                            el.medical
                          }
                          displayType={"text"}
                          thousandSpacing={"2s"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                          suffix={"/-"}
                          renderText={(value) => <span>{value}</span>}
                        />
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={periodMode === "yearly" ? "5" : "4"}></td>

                    <td colSpan="1" className="expenses-num">
                      Total Salary :{" "}
                      <CurrencyFormat
                        value={totalSalary}
                        displayType={"text"}
                        thousandSpacing={"2s"}
                        thousandSeparator={true}
                        prefix={"Rs "}
                        suffix={"/-"}
                        renderText={(value) => <span>{value}</span>}
                      />
                    </td>
                    <td colSpan="1"></td>
                    {designtion === "Trainer" ? (
                      <>
                        <td colSpan="1" className="expenses-num">
                          Total PT Commission :{" "}
                          <CurrencyFormat
                            value={items.reduce(
                              (a, v) => (a = a + parseInt(v.pt_commission)),
                              0
                            )}
                            displayType={"text"}
                            thousandSpacing={"2s"}
                            thousandSeparator={true}
                            prefix={"Rs "}
                            suffix={"/-"}
                            renderText={(value) => <span>{value}</span>}
                          />
                        </td>
                        <td colSpan="4"></td>
                      </>
                    ) : null}

                    <td colSpan="1" className="expenses-num">
                      Total Advance :{" "}
                      <CurrencyFormat
                        value={items.reduce(
                          (a, v) => (a = a + parseInt(v.advance_total)),
                          0
                        )}
                        displayType={"text"}
                        thousandSpacing={"2s"}
                        thousandSeparator={true}
                        prefix={"Rs "}
                        suffix={"/-"}
                        renderText={(value) => <span>{value}</span>}
                      />
                    </td>
                    <td colSpan="1" className="expenses-num">
                      Total Fine :{" "}
                      <CurrencyFormat
                        value={items.reduce(
                          (a, v) => (a = a + parseInt(v.fine_total)),
                          0
                        )}
                        displayType={"text"}
                        thousandSpacing={"2s"}
                        thousandSeparator={true}
                        prefix={"Rs "}
                        suffix={"/-"}
                        renderText={(value) => <span>{value}</span>}
                      />
                    </td>
                    <td colSpan="1" className="expenses-num">
                      Total Monthly Installment :{" "}
                      <CurrencyFormat
                        value={items.reduce(
                          (a, v) => (a = a + parseInt(v.loans)),
                          0
                        )}
                        displayType={"text"}
                        thousandSpacing={"2s"}
                        thousandSeparator={true}
                        prefix={"Rs "}
                        suffix={"/-"}
                        renderText={(value) => <span>{value}</span>}
                      />
                    </td>
                    <td colSpan="1"></td>
                    <td colSpan="1" className="expenses-num">
                      Total Net Salary :{" "}
                      <CurrencyFormat
                        // value={totalNetSalary}
                        value={Math.ceil(totalNetSalary)}
                        displayType={"text"}
                        thousandSpacing={"2s"}
                        thousandSeparator={true}
                        prefix={"Rs "}
                        suffix={"/-"}
                        renderText={(value) => <span>{value}</span>}
                      />
                    </td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td colSpan="14" className="text-center text-capitalize">
                    No record found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {totalResults > limit && totalPages > 1 ? (
          <Pagination
            activePage={currentPage}
            itemsCountPerPage={parseInt(limit)}
            totalItemsCount={totalResults}
            onChange={(e) => {
              changePagination(e);
            }}
            pageRangeDisplayed={8}
            itemClass="page-item"
            linkClass="page-link"
            firstPageText="First Page"
            lastPageText="Last Page"
          />
        ) : (
          ""
        )}
      </>
    );
  };
  return (
    <>
      <div className="card-body">
        <h5 className="mb-0 text-danger mt-2">View Salary</h5>
        <form className="row g-3 trainee-add mt-3" onSubmit={handleSubmit}>
          <div className="col-md-2">
            <div className="form-group custom-group">
              <label className="form-label">Period</label>
              <select
                className="form-select"
                value={periodMode}
                onChange={(e) => {
                  const mode = e.target.value;
                  setPeriodMode(mode);
                  getSalary(mode, values);
                }}
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
          </div>
          {periodMode === "monthly" ? (
            <div className="col-md-2">
              <div className="form-group custom-group">
                <label className="form-label">Select Month</label>
                <select
                  className="form-select"
                  name="sdate"
                  value={values.sdate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <option value="01">January</option>
                  <option value="02">February</option>
                  <option value="03">March</option>
                  <option value="04">April</option>
                  <option value="05">May</option>
                  <option value="06">June</option>
                  <option value="07">July</option>
                  <option value="08">August</option>
                  <option value="09">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>

                {errors.sdate && touched.sdate ? (
                  <p className="text-danger">{errors.sdate}</p>
                ) : null}
              </div>
            </div>
          ) : null}
          <div className="col-md-2">
            <div className="form-group custom-group">
              <label className="form-label">Enter Year</label>
              <input
                type="number"
                className="form-control"
                placeholder="Enter Year"
                name="edate"
                value={values.edate}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              {errors.edate && touched.edate ? (
                <p className="text-danger">{errors.edate}</p>
              ) : null}
            </div>
          </div>
          <div className="col-12 col-md-1">
            <button
              type="submit"
              className="btn btn-primary d-inline-block mt-4"
            >
              Generate
            </button>
          </div>
          {totalResults > limit ? (
            <div className="col-md-1 col-lg-1 mt-5 ms-auto">
              <div className="input-group">
                <select
                  className="form-select"
                  value={limit}
                  onChange={(e) => changeLimit(e.target.value)}
                >
                  <option value="10">10</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="250">250</option>
                  <option value="500">500</option>
                </select>
              </div>
            </div>
          ) : null}
          <div className="ms-auto col-md-1 text-end mt-5">
            <button
              className="btn btn-primary mx-2 file-download"
              onClick={() => {
                generatePdf(document.getElementById("report-POS"));
              }}
            >
              <i className="bi bi-file-earmark-pdf"></i> PDF
            </button>
          </div>
        </form>
        <Table data={myData} rowsPerPage={limit} />
      </div>
    </>
  );
}
